import classNames from 'classnames';
import { ImgHTMLAttributes, ReactNode, useCallback, useEffect, useMemo } from 'react';

import useLoadingStatus from '../../hooks/useLoadingStatus';
import { ImageUrlBuilder, ScalingMode } from '../../ImageUrlBuilder';
import DefaultImage from '../DefaultImage/DefaultImage';
import css from './Image.css';

interface ImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'placeholder' | 'onError' | 'onLoad'> {
  src: string;
  classNameError?: string;
  classNameFallbackElement?: string;
  classNameImageElement?: string;
  classNameUnloaded?: string;
  fallback?: ReactNode;
  height?: number;
  placeholder?: ReactNode;
  sharpeningFactor?: number;
  width?: number;
  size?: 'auto' | 'contain' | 'cover';
  scalingMode?: ScalingMode;
}

const Image = ({
  className,
  classNameError,
  classNameFallbackElement,
  classNameImageElement,
  classNameUnloaded,
  fallback,
  height,
  onClick,
  placeholder,
  sharpeningFactor,
  scalingMode,
  src,
  width,
  alt = '',
  size = 'auto',
  ...props
}: ImageProps) => {
  const isCustomPlaceholder = placeholder !== undefined;

  const { isError, isLoaded, isLoading, setError, setInProgress, setSuccess } = useLoadingStatus();

  const url = useMemo<string>(
    () => ImageUrlBuilder.build(src, width, height, sharpeningFactor, scalingMode),
    [height, sharpeningFactor, src, width],
  );

  const handleImageLoadingStatus = useCallback((imageElement: HTMLImageElement) => {
    if (!imageElement) {
      return;
    }

    if (imageElement.complete) {
      if (imageElement.naturalHeight > 0) {
        setSuccess();
      } else {
        setError();
      }
    }

    imageElement.onload = setSuccess;
    imageElement.onerror = setError;
  }, []);

  useEffect(() => {
    // Nothing to load: show the fallback. A reused instance — a recycled row — may still hold
    // the previous image's status, and an `<img>` without `src` never fires load or error.
    if (!url) {
      setError();
    } else if (isError) {
      setInProgress();
    }
  }, [url]);

  return (
    <div
      className={classNames(
        css.wrapper,
        {
          [css.withPlaceholder]: isLoading && !isCustomPlaceholder,
          [classNameUnloaded]: !isLoaded,
          [classNameError]: isError,
        },
        className,
      )}
    >
      {isError ? (
        (fallback ?? <DefaultImage className={classNameFallbackElement} />)
      ) : (
        <>
          <img
            ref={handleImageLoadingStatus}
            className={classNames(
              css.element,
              {
                [css.contain]: size === 'contain',
                [css.cover]: size === 'cover',
              },
              classNameImageElement,
            )}
            src={url || null}
            alt={alt}
            onClick={onClick}
            {...props}
          />
          {isLoading && placeholder}
        </>
      )}
    </div>
  );
};

export default Image;
